import { useCallback, useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { Popover } from '@/mastodon/components/popover';
import AddIcon from '@/material-icons/400-24px/add.svg?react';
import CircleGapIcon from '@/material-icons/400-24px/circle_gap.svg?react';
import GroupsIcon from '@/material-icons/400-24px/groups.svg?react';
import ListAltIcon from '@/material-icons/400-24px/list_alt.svg?react';
import LockIcon from '@/material-icons/400-24px/lock.svg?react';
import SettingsIcon from '@/material-icons/400-24px/settings.svg?react';
import {
  changeComposeGroup,
  changeComposeAudienceList,
  changeComposeList,
} from 'mastodon/actions/compose_typed';
import { fetchLists } from 'mastodon/actions/lists';
import { Icon } from 'mastodon/components/icon';
import { fetchMyAudienceLists } from 'mastodon/reducers/slices/audience_lists';
import { fetchMyGroups } from 'mastodon/reducers/slices/groups';
import { selectMyAudienceLists } from 'mastodon/selectors/audience_lists';
import { selectMyGroups } from 'mastodon/selectors/groups';
import { getOrderedLists } from 'mastodon/selectors/lists';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

const messages = defineMessages({
  label: { id: 'compose.group.change', defaultMessage: 'Post to a group' },
  none: { id: 'compose.group.none', defaultMessage: 'My Timeline' },
  locked: {
    id: 'compose.group.locked',
    defaultMessage: 'Posting to this group',
  },
  groupsHeading: {
    id: 'compose.audience.groups_heading',
    defaultMessage: 'Groups',
  },
  audienceListsHeading: {
    id: 'compose.audience.audience_lists_heading',
    defaultMessage: 'Custom audiences',
  },
  newAudienceList: {
    id: 'compose.audience.new_audience_list',
    defaultMessage: 'New audience list…',
  },
  editAudienceList: {
    id: 'compose.audience.edit_audience_list',
    defaultMessage: 'Edit audience list',
  },
  listsHeading: {
    id: 'compose.audience.lists_heading',
    defaultMessage: 'Lists',
  },
  newList: {
    id: 'compose.audience.new_list',
    defaultMessage: 'New list…',
  },
  editList: {
    id: 'compose.audience.edit_list',
    defaultMessage: 'Edit list',
  },
});

export const GroupDropdown: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const groups = useAppSelector(selectMyGroups);
  const audienceLists = useAppSelector(selectMyAudienceLists);
  const lists = useAppSelector(getOrderedLists);
  const groupId = useAppSelector(
    (state) => state.compose.get('group_id') as string | null,
  );
  const audienceListId = useAppSelector(
    (state) => state.compose.get('audience_list_id') as string | null,
  );
  const listId = useAppSelector(
    (state) => state.compose.get('list_id') as string | null,
  );
  // Locked while composing on a group page (audience is fixed to that group)
  // or while editing an existing post -- the update endpoint can't change a
  // post's group/audience, so the picker just displays the true value here
  // instead of implying it's editable.
  const locked = useAppSelector(
    (state) =>
      Boolean(state.compose.get('group_locked')) || !!state.compose.get('id'),
  );
  // The targeted group may not be in the (paginated / still-loading) "my groups"
  // list — e.g. the inline composer on a group page targets whatever group is
  // open. Fall back to the normalized group store so the label and menu still
  // resolve it.
  const targetedGroup = useAppSelector((state) =>
    groupId ? state.groups.groups[groupId] : undefined,
  );
  const targetedAudienceList = useAppSelector((state) =>
    audienceListId
      ? state.audienceLists.audienceLists[audienceListId]
      : undefined,
  );
  const targetedList = useAppSelector((state) =>
    listId ? (state.lists.get(listId) ?? undefined) : undefined,
  );

  useEffect(() => {
    void dispatch(fetchMyGroups());
    void dispatch(fetchMyAudienceLists());
    void dispatch(fetchLists());
  }, [dispatch]);

  const handleToggle = useCallback(() => {
    if (locked) return;
    setOpen((value) => !value);
  }, [locked]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelectNone = useCallback(() => {
    dispatch(changeComposeGroup(null));
    dispatch(changeComposeAudienceList(null));
    dispatch(changeComposeList(null));
    setOpen(false);
  }, [dispatch]);

  const handleSelectGroup = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const id = e.currentTarget.dataset.groupId ?? '';
      dispatch(changeComposeGroup(id === '' ? null : id));
      setOpen(false);
    },
    [dispatch],
  );

  const handleSelectAudienceList = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const id = e.currentTarget.dataset.audienceListId ?? '';
      dispatch(changeComposeAudienceList(id === '' ? null : id));
      setOpen(false);
    },
    [dispatch],
  );

  const handleSelectList = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const id = e.currentTarget.dataset.listId ?? '';
      dispatch(changeComposeList(id === '' ? null : id));
      setOpen(false);
    },
    [dispatch],
  );

  const currentGroup =
    groups.find((group) => group.id === groupId) ?? targetedGroup;

  const currentAudienceList =
    audienceLists.find((list) => list.id === audienceListId) ??
    targetedAudienceList;

  const currentList = lists.find((list) => list.id === listId) ?? targetedList;

  // Unlike groups (only relevant once you've joined one), the "new audience
  // list" menu item means this dropdown is always worth showing so it stays
  // discoverable, even before the user has created anything.

  // Ensure the currently-targeted group/audience list is always offered in
  // the menu, even when it isn't part of the loaded lists.
  const menuGroups =
    currentGroup && !groups.some((group) => group.id === currentGroup.id)
      ? [currentGroup, ...groups]
      : groups;

  const menuAudienceLists =
    currentAudienceList &&
    !audienceLists.some((list) => list.id === currentAudienceList.id)
      ? [currentAudienceList, ...audienceLists]
      : audienceLists;

  const menuLists =
    currentList && !lists.some((list) => list.id === currentList.id)
      ? [currentList, ...lists]
      : lists;

  const currentLabel = currentAudienceList
    ? currentAudienceList.name
    : currentList
      ? currentList.title
      : currentGroup
        ? currentGroup.display_name
        : intl.formatMessage(messages.none);

  return (
    <div className='group-dropdown'>
      <button
        type='button'
        ref={setTargetElement}
        title={intl.formatMessage(locked ? messages.locked : messages.label)}
        aria-expanded={locked ? undefined : open}
        aria-disabled={locked}
        disabled={locked}
        onClick={handleToggle}
        className={classNames('dropdown-button', {
          active:
            !locked && (open || !!groupId || !!audienceListId || !!listId),
          'dropdown-button--locked': locked,
        })}
      >
        {currentAudienceList ? (
          <Icon id='circle_gap' icon={CircleGapIcon} noFill />
        ) : currentList ? (
          <Icon id='list_alt' icon={ListAltIcon} />
        ) : (
          <Icon id='group' icon={GroupsIcon} />
        )}
        <span className='dropdown-button__label'>{currentLabel}</span>
      </button>

      <Popover
        isOpen={open && !locked}
        onClose={handleClose}
        offset={5}
        reference={targetElement}
      >
        {({ props, placement }) => (
          <div {...props}>
            <div
              className={`dropdown-animation dropdown-menu group-dropdown__menu ${placement}`}
            >
              <button
                type='button'
                className={classNames('group-dropdown__menu__item', {
                  active: !groupId && !audienceListId && !listId,
                })}
                onClick={handleSelectNone}
              >
                {intl.formatMessage(messages.none)}
              </button>

              {menuGroups.length > 0 && (
                <>
                  <div className='group-dropdown__menu__heading'>
                    {intl.formatMessage(messages.groupsHeading)}
                  </div>
                  {menuGroups.map((group) => (
                    <button
                      key={group.id}
                      type='button'
                      data-group-id={group.id}
                      className={classNames('group-dropdown__menu__item', {
                        active: group.id === groupId,
                      })}
                      onClick={handleSelectGroup}
                    >
                      {group.private && <Icon id='lock' icon={LockIcon} />}
                      {group.display_name}
                    </button>
                  ))}
                </>
              )}

              <div className='group-dropdown__menu__heading'>
                {intl.formatMessage(messages.audienceListsHeading)}
              </div>
              {menuAudienceLists.map((list) => (
                <div key={list.id} className='group-dropdown__menu__row'>
                  <button
                    type='button'
                    data-audience-list-id={list.id}
                    className={classNames('group-dropdown__menu__item', {
                      active: list.id === audienceListId,
                    })}
                    onClick={handleSelectAudienceList}
                  >
                    <Icon id='circle_gap' icon={CircleGapIcon} noFill />
                    {list.name}
                  </button>
                  <Link
                    to={`/audience_lists/${list.id}/edit`}
                    className='group-dropdown__menu__row__edit'
                    title={intl.formatMessage(messages.editAudienceList)}
                    aria-label={intl.formatMessage(messages.editAudienceList)}
                    onClick={handleClose}
                  >
                    <Icon id='settings' icon={SettingsIcon} />
                  </Link>
                </div>
              ))}
              <Link
                to='/audience_lists/new'
                className='group-dropdown__menu__item'
                onClick={handleClose}
              >
                <Icon id='add' icon={AddIcon} />
                {intl.formatMessage(messages.newAudienceList)}
              </Link>

              <div className='group-dropdown__menu__heading'>
                {intl.formatMessage(messages.listsHeading)}
              </div>
              {menuLists.map((list) => (
                <div key={list.id} className='group-dropdown__menu__row'>
                  <button
                    type='button'
                    data-list-id={list.id}
                    className={classNames('group-dropdown__menu__item', {
                      active: list.id === listId,
                    })}
                    onClick={handleSelectList}
                  >
                    <Icon id='list_alt' icon={ListAltIcon} />
                    {list.title}
                  </button>
                  <Link
                    to={`/lists/${list.id}/edit`}
                    className='group-dropdown__menu__row__edit'
                    title={intl.formatMessage(messages.editList)}
                    aria-label={intl.formatMessage(messages.editList)}
                    onClick={handleClose}
                  >
                    <Icon id='settings' icon={SettingsIcon} />
                  </Link>
                </div>
              ))}
              <Link
                to='/lists/new'
                className='group-dropdown__menu__item'
                onClick={handleClose}
              >
                <Icon id='add' icon={AddIcon} />
                {intl.formatMessage(messages.newList)}
              </Link>
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};
